<template>
    <div>
        <!-- Full-screen loading overlay -->
        <v-overlay v-model="pageLoading" class="align-center justify-center" persistent>
            <div class="text-center">
                <v-progress-circular
                    indeterminate
                    size="64"
                    color="primary"
                ></v-progress-circular>
                <div class="mt-4 text-h6">Đang tải dữ liệu...</div>
            </div>
        </v-overlay>

        <!-- Card thêm category -->
        <v-card class="mb-6">
            <v-card-title class="text-h6">
                <v-icon class="mr-2">mdi-plus-circle</v-icon>
                Thêm danh mục mới
            </v-card-title>
            <v-card-text>
                <v-form ref="categoryForm">
                    <v-row>
                        <v-col md="12">
                            <v-text-field v-model="newCategory" label="Tên danh mục" placeholder="Nhập tên danh mục..."
                                variant="outlined" density="compact"
                                :rules="[v => !!v || 'Tên danh mục là bắt buộc']"></v-text-field>
                        </v-col>
                    </v-row>
                </v-form>
                <v-row>
                    <v-col cols="12" class="d-flex justify-center">
                        <v-btn color="primary" variant="elevated" @click="addCategory" :disabled="!newCategory"
                            :loading="loading">
                            <v-icon class="mr-2">mdi-plus</v-icon>
                            Tạo mới
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <!-- Table danh sách category -->
        <v-card>
            <v-card-title class="text-h6">
                <v-icon class="mr-2">mdi-folder-multiple</v-icon>
                Danh sách danh mục
            </v-card-title>
            <v-card-text>
                <!-- Desktop Table View -->
                <div v-if="!$vuetify.display.mobile" class="desktop-view">
                    <v-table class="elevation-1">
                        <thead>
                            <tr>
                                <th class="text-left">Tên danh mục</th>
                                <th class="text-center">Số lượng sản phẩm</th>
                                <th class="text-center">Trạng thái</th>
                                <th class="text-center">Thao tác</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="category in allCategories" :key="category.id">
                                <td>{{ category.name }}</td>
                                <td class="text-center">{{ category.productCount }}</td>
                                <td class="text-center">
                                    <v-chip :color="category.status === 1 ? 'success' : 'error'" size="small">
                                        {{ category.status === 1 ? 'Hoạt động' : 'Tạm ẩn' }}
                                    </v-chip>
                                </td>
                                <td class="text-center">
                                    <div class="d-flex justify-center">
                                        <v-btn icon size="small" color="primary" @click="editCategory(category)"
                                            class="mr-2">
                                            <v-icon>mdi-pencil</v-icon>
                                        </v-btn>
                                        <v-btn icon size="small" :color="category.status === 0 ? 'error' : 'success'"
                                            @click="toggleStatus(category)">
                                            <v-icon>
                                                {{ category.status === 0 ? 'mdi-eye-off' : 'mdi-eye' }}
                                            </v-icon>
                                        </v-btn>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </v-table>
                </div>

                <!-- Mobile Card View -->
                <div v-else class="mobile-view">
                    <div class="mobile-categories-container" ref="categoriesContainer">
                        <v-card 
                            v-for="category in allCategories" 
                            :key="category.id" 
                            class="mb-3 category-card"
                            elevation="2">
                            <v-card-text>
                                <div class="d-flex justify-space-between align-center mb-2">
                                    <div class="category-name font-weight-bold">{{ category.name }}</div>
                                    <v-chip :color="category.status === 1 ? 'success' : 'error'" size="small">
                                        {{ category.status === 1 ? 'Hoạt động' : 'Tạm ẩn' }}
                                    </v-chip>
                                </div>
                                
                                <div class="category-info">
                                    <div class="info-row">
                                        <v-icon size="16" color="info" class="mr-2">mdi-package-variant</v-icon>
                                        <strong>Số sản phẩm:</strong>
                                        <span class="ml-2">{{ category.productCount || 0 }}</span>
                                    </div>
                                </div>
                                
                                <div class="d-flex justify-center mt-3 gap-2">
                                    <v-btn color="primary" size="small" @click="editCategory(category)">
                                        <v-icon class="mr-1">mdi-pencil</v-icon>
                                        Sửa
                                    </v-btn>
                                    <v-btn :color="category.status === 0 ? 'error' : 'success'" size="small" @click="toggleStatus(category)">
                                        <v-icon class="mr-1">{{ category.status === 0 ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                                        {{ category.status === 0 ? 'Ẩn' : 'Hiện' }}
                                    </v-btn>
                                </div>
                            </v-card-text>
                        </v-card>
                    </div>
                    
                    <!-- Loading indicator for infinite scroll -->
                    <div v-if="loadingMore" class="text-center py-4">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                        <div class="mt-2">Đang tải thêm...</div>
                    </div>
                    
                    <!-- End of list indicator -->
                    <div v-if="hasReachedEnd && allCategories.length > 0" class="text-center py-4 text-grey">
                        <v-icon>mdi-check-circle</v-icon>
                        <div>Đã hiển thị tất cả danh mục</div>
                    </div>
                </div>
            </v-card-text>
        </v-card>

        <!-- Dialog edit category -->
        <v-dialog v-model="editDialog" max-width="400" persistent>
            <v-card v-if="editingCategory.name !== undefined">
                <v-card-title class="text-h6">
                    <v-icon class="mr-2">mdi-pencil</v-icon>
                    Chỉnh sửa danh mục
                </v-card-title>
                <v-card-text>
                    <v-text-field v-model="editingCategory.name" label="Tên danh mục" variant="outlined"
                        density="compact" :rules="[v => !!v || 'Tên danh mục là bắt buộc']"></v-text-field>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="grey" variant="text" @click="closeEditDialog">
                        Hủy
                    </v-btn>
                    <v-btn color="primary" variant="elevated" @click="updateCategory" :loading="updateLoading">
                        Cập nhật
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from '../http';
export default {
    name: 'Category',
    data() {
        return {
            newCategory: '',
            loading: false,
            tableLoading: false,
            pageLoading: false, // Full-screen loading state
            editDialog: false,
            updateLoading: false,
            editingCategory: { name: '' },

            categories: [],
            allCategories: [], // For infinite scroll
            loadingMore: false, // For infinite scroll loading
            hasReachedEnd: false, // Track if we've loaded all data
            currentMobilePage: 1, // Separate page tracking for mobile
            page: 1,
            limit: 10,
            totalPages: 1
        }
    },
    methods: {
        async fetchCategories(showPageLoading = false, append = false) {
            if (showPageLoading) {
                this.pageLoading = true;
            } else if (append) {
                this.loadingMore = true;
            } else {
                this.tableLoading = true;
            }
            
            try {
                const response = await axios.get('/categories', {
                    params: { 
                        page: append ? this.currentMobilePage : this.page, 
                        limit: this.limit 
                    }
                });
                
                if (append) {
                    // For mobile infinite scroll
                    this.allCategories = [...this.allCategories, ...response.data.data || response.data];
                    this.hasReachedEnd = (response.data.data || response.data).length < this.limit;
                } else {
                    // For desktop or initial load
                    const data = response.data.data || response.data;
                    this.categories = data;
                    this.allCategories = data;
                }
            } catch (error) {
                console.error('Error fetching categories:', error);
            } finally {
                if (showPageLoading) {
                    this.pageLoading = false;
                } else if (append) {
                    this.loadingMore = false;
                } else {
                    this.tableLoading = false;
                }
            }
        },

        async loadMoreCategories() {
            if (this.loadingMore || this.hasReachedEnd) return;
            
            this.currentMobilePage++;
            await this.fetchCategories(false, true);
        },

        resetMobileData() {
            this.allCategories = [];
            this.currentMobilePage = 1;
            this.hasReachedEnd = false;
        },
        async addCategory() {
            if (!this.newCategory) return

            this.loading = true
            try {
                // Gọi API tạo category
                const response = await axios.post('/categories', { name: this.newCategory });
                const newCat = response.data;
                this.categories.unshift({
                    id: newCat.id,
                    name: newCat.name,
                    productCount: 0,
                    status: newCat.status || 'active'
                });
                this.newCategory = '';
                // Reset validation sau khi clear form
                this.$refs.categoryForm.resetValidation();
                // Show success message
                console.log('Category added successfully');
            } catch (error) {
                console.error('Error adding category:', error);
            } finally {
                this.loading = false;
            }
        },

        editCategory(category) {
            this.editingCategory = { ...category }
            this.editDialog = true
        },

        async updateCategory() {
            if (!this.editingCategory.name) return

            this.updateLoading = true
            try {
                // Gọi API cập nhật category
                await axios.put(`/categories/${this.editingCategory.id}`, { name: this.editingCategory.name });
                
                const index = this.categories.findIndex(cat => cat.id === this.editingCategory.id)
                if (index !== -1) {
                    this.categories[index].name = this.editingCategory.name;
                }
                this.editDialog = false
                console.log('Category updated successfully')
            } catch (error) {
                console.error('Error updating category:', error)
            } finally {
                this.updateLoading = false
            }
        },

        async toggleStatus(category) {
            try {
                const newStatus = category.status === 1 ? 0 : 1;
                await axios.put(`/categories/${category.id}/status`, { status: newStatus });
                const index = this.categories.findIndex(cat => cat.id === category.id);
                if (index !== -1) {
                    this.categories[index].status = newStatus;
                }
                console.log('Status toggled successfully');
            } catch (error) {
                console.error('Error toggling status:', error);
            }
        },

        closeEditDialog() {
            this.editDialog = false
            this.editingCategory = { name: '' }
        },

        setupInfiniteScroll() {
            if (!('IntersectionObserver' in window)) return;
            
            this.$nextTick(() => {
                if (this.$refs.categoriesContainer) {
                    this.observer = new IntersectionObserver((entries) => {
                        const target = entries[0];
                        if (target.isIntersecting && this.$vuetify.display.mobile) {
                            this.loadMoreCategories();
                        }
                    }, {
                        root: null,
                        rootMargin: '200px',
                        threshold: 0.1
                    });
                    
                    // Create a sentinel element at the bottom
                    const sentinel = document.createElement('div');
                    sentinel.style.height = '1px';
                    sentinel.style.background = 'transparent';
                    this.$refs.categoriesContainer?.appendChild(sentinel);
                    this.observer.observe(sentinel);
                }
            });
        }
    },
    mounted() {
        this.fetchCategories(true); // Show page loading on initial load
        this.setupInfiniteScroll();
    },
    beforeUnmount() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }
}
</script>

<style scoped>
/* Mobile Card Styles */
.mobile-view {
  padding: 0;
}

.mobile-categories-container {
  padding: 8px 0;
}

.category-card {
  border-radius: 12px !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.category-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.category-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  min-height: 24px;
  font-size: 14px;
}

.info-row strong {
  min-width: 70px;
  font-weight: 600;
}

.info-row .ml-2 {
  flex: 1;
  word-break: break-word;
}

.category-name {
  word-break: break-word;
  line-height: 1.3;
}

/* Desktop responsive adjustments */
@media (max-width: 1200px) {
  .desktop-view .v-table thead th {
    font-size: 13px;
    padding: 8px 4px;
  }
  
  .desktop-view .v-table tbody td {
    font-size: 13px;
    padding: 8px 4px;
  }
}

/* Mobile specific styles */
@media (max-width: 768px) {
  .mobile-categories-container {
    padding: 4px 0;
  }
  
  .category-card {
    margin-bottom: 12px !important;
  }
  
  .info-row {
    font-size: 13px;
  }
  
  .info-row strong {
    min-width: 65px;
    font-size: 13px;
  }
  
  .category-name {
    font-size: 15px;
  }
}
</style>